import React, { useContext, useState, useEffect } from "react"
import { StateContext } from "../el/provider"
import { twMerge } from "tailwind-merge"
import { motion, AnimatePresence } from "framer-motion"
import { HashtagIcon } from "@heroicons/react/20/solid"
import { ShoppingCartIcon } from "@heroicons/react/24/outline"
import { ShoppingBagIcon } from "@heroicons/react/24/outline"
import googleTag from "../googleTag"
const ProductList = props => {
  return (
    <div className="bg-white shadow-sm sm:rounded-md md:shadow-lg">
      <div className="overflow-hidden shadow sm:rounded-md md:shadow-md">
        <ul>
          {props.productQuery.edges.map(prod => {
            return (
              <LineItem
                key={prod.node.id}
                borderColor={props.borderColor}
                image={prod.node.image}
                prod={prod.node}
                alt={prod.node.title}
              />
            )
          })}
        </ul>
      </div>
    </div>
  )
}

// main parts
const LineItem = props => {
  const { stateBag, stateAlert, stateCart } = useContext(StateContext)
  const [bag, setBag] = stateBag // eslint-disable-line no-unused-vars
  const [pageAlert, setPageAlert] = stateAlert // eslint-disable-line no-unused-vars
  const [cart, setCart] = stateCart // eslint-disable-line no-unused-vars
  const [showInfo, setShowInfo] = useState(false)
  const [added, setAdded] = useState(false)
  //add item to quote/order
  const addToBag = (item, type) => {
    const addAlert = {
      button: false,
      type: "success",
      title: `${item.title}`,
      message: `Added to ${type ? `cart` : `quote`}`,
    }
    const typeText = type ? `cart` : `quote`
    setCart(type)
    setAdded(true)
    setBag(currentBag => [...currentBag, item])
    setPageAlert(addAlert)
    googleTag(typeText, { item: item.title, partNum: item.partNumber[0].num })
  }

  const pnCount = props.prod.partNumber.length
  const infoVariants = {
    initial: {
      originY: 0,
      scaleY: 0,
      opacity: 0,
    },
    show: {
      originY: 0,
      scaleY: 1,
      opacity: 1,
      transition: { type: "tween", duration: 0.125, ease: "easeOut" },
    },
    exit: {
      originY: 0,
      scaleY: 0,
      opacity: 0,
      transition: { type: "tween", duration: 0.125, ease: "easeOut" },
    },
  }

  return (
    <li className="border-t first-of-type:border-t-0" id={props.prod.id}>
      <div className="group block w-full text-left hover:bg-slate-50 focus:outline-none">
        <div className="p-1 pb-0 pt-2.5 md:pl-0 md:pr-4 xl:px-4">
          <div className="grid grid-cols-1 md:grid-cols-12 md:gap-4">
            <ProdDetails
              onClick={() => {
                setShowInfo(!showInfo)
              }}
              role="presentation"
              aria-hidden="true"
            >
              <ProdImg
                borderColor={props.borderColor}
                image={props.image}
                alt={props.alt}
              ></ProdImg>

              <DetailSection className="col-span-8 row-start-1 mb-3">
                <Title>{props.prod.title}</Title>
                <Sterile sterile={props.prod.sterile}></Sterile>
              </DetailSection>

              <DetailSection className="col-span-6 col-start-3 row-span-2 row-start-2 pl-4 md:pl-0">
                <DetailBlock className="">
                  <DetailItem className="md:mr-6">
                    {props.prod.desc_main}
                  </DetailItem>
                  <DetailItem>{props.prod.desc_sub}</DetailItem>
                </DetailBlock>

                <DetailBlock className="mt-1 md:mt-0">
                  <DetailItem>
                    <Pn />
                    {props.prod.partNumber[0].num}
                    <Pipe />
                    {props.prod.partNumber[0].qty}
                    <Case />
                  </DetailItem>
                  {pnCount > 1 ? (
                    <DetailItem>
                      <Pn />
                      {props.prod.partNumber[1].num}
                      <Pipe />
                      {props.prod.partNumber[1].qty}
                      <Case />
                    </DetailItem>
                  ) : (
                    " "
                  )}
                </DetailBlock>
              </DetailSection>
            </ProdDetails>

            <ProdAction
              disabled={added}
              quoteClick={() => addToBag(props.prod, false)}
              cartClick={() => addToBag(props.prod, true)}
            ></ProdAction>
          </div>
        </div>
        <button
          onClick={() => {
            setShowInfo(!showInfo)
          }}
          className="block w-full cursor-pointer pb-2 pt-4 text-center text-xs text-slate-200 group-hover:text-slate-400 md:py-1"
        >
          click for more details
        </button>
      </div>
      <AnimatePresence>
        {showInfo && (
          <motion.div
            variants={infoVariants}
            initial="initial"
            animate="show"
            exit="exit"
            className="prodInfo overflow-hidden border-t border-slate-200 bg-slate-50 px-4 py-4 shadow-inner sm:px-6"
          >
            <div className="mb-2 pl-0 text-center text-sm font-light text-slate-700 md:pl-2 md:text-left">
              Specifications:
            </div>
            <div className="grid grid-cols-1 gap-2 md:grid-cols-6 lg:grid-cols-5 lg:gap-4">
              <div className="grid-cols-1 md:col-span-2 lg:col-span-1">
                <div
                  className={twMerge([
                    props.borderColor,
                    "mx-auto h-auto w-3/4 overflow-hidden rounded-sm md:mx-0 md:w-full lg:rounded-md",
                  ])}
                >
                  {" "}
                  <img
                    src={`/product-images/${props.image}`}
                    className="rounded-sm"
                    alt={props.alt}
                  />
                </div>
              </div>

              <div className="col-span-1 overflow-hidden rounded-sm md:col-span-4 lg:rounded-md">
                <table className="w-full">
                  <tbody>
                    {Object.entries(props.prod.specs).map((spec, index) => {
                      /* if value is BOOLEAN */
                      if (typeof spec[1].value === "boolean") {
                        if (spec[1].value) {
                          spec[1].value = "Yes"
                        } else {
                          spec[1].value = "No"
                        }
                      }

                      /* if value is ARRAY */
                      if (Array.isArray(spec[1].value)) {
                        let valueLength = spec[1].value.length
                        let hold = ""
                        for (var i = 0; i < valueLength - 1; i++) {
                          hold += spec[1].value[i] + ", "
                        }
                        hold += spec[1].value[valueLength - 1]
                        spec[1].value = hold
                      }
                      /* RETURN EVERYTHING */
                      return (
                        <tr
                          className="border-b border-slate-200 last:border-b-0"
                          key={index}
                        >
                          <SpecItem>{spec[1].key}</SpecItem>
                          <Spec>: {spec[1].value}</Spec>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </li>
  )
}
const ProdImg = props => {
  return (
    <div
      role="presentation"
      aria-hidden="true"
      className="col-span-2 row-span-2 row-start-2 flex w-auto items-start justify-center md:row-start-1 md:items-end md:self-end xl:col-auto"
    >
      <div
        className={twMerge([
          props.borderColor,
          "mx-auto h-20 w-20 rounded-md shadow-sm ring-2 ring-offset-1 ring-offset-slate-300 md:h-16 md:w-16 md:rounded-full md:shadow-md xl:ml-0 xl:mr-auto",
        ])}
      >
        <img
          src={`/product-thumbnails/${props.image}`}
          alt={props.alt}
          className="rounded-md md:rounded-full w-20 h-20 md:w-16 md:h-16 object-cover"
        />
      </div>
    </div>
  )
}
const ProdDetails = props => {
  return (
    <button
      onClick={props.onClick}
      className="productButton col-span-1 grid cursor-pointer grid-cols-8 grid-rows-3 md:col-span-10 md:grid-cols-12 md:grid-rows-2"
    >
      {props.children}
    </button>
  )
}

const DetailSection = props => {
  return (
    <div
      className={twMerge([
        "flex flex-col text-left md:col-span-10 md:flex-row md:items-center md:justify-between xl:col-span-11",
        props.className,
      ])}
    >
      {props.children}
    </div>
  )
}
const DetailItem = props => {
  return (
    <div
      className={twMerge(["text-sm leading-5 text-slate-500", props.className])}
    >
      {props.children}
    </div>
  )
}
const DetailBlock = props => {
  return (
    <div className={twMerge(["md:flex md:flex-col", props.className])}>
      {props.children}
    </div>
  )
}

const ActionButton = props => {
  const { stateCart, stateBag } = useContext(StateContext)
  const [cart] = stateCart
  const [bag] = stateBag
  const [btnState, setBtnState] = useState("alone")
  const actionBtnVariants = {
    initial: {
      opacity: 1,
      scale: 1,
    },
    exit: {
      x: 100,
      opacity: 0,
      transition: { duration: 0.5 },
    },
    alone: {
      scale: [1.1, 1, 1.06],
    },
  }
  useEffect(() => {
    if (bag.length === 0) {
      setBtnState("initial")
    } else {
      cart && props.type === "order" && setBtnState("alone")
      cart && props.type === "quote" && setBtnState("exit")
      !cart && props.type === "order" && setBtnState("exit")
      !cart && props.type === "quote" && setBtnState("alone")
    }
  }, [cart, bag, props.type])
  return (
    <motion.div
      className="w-1/2  rounded-md md:w-full md:shadow-sm"
      variants={actionBtnVariants}
      initial="initial"
      animate={btnState}
      type={props.type}
    >
      <button
        onClick={props.actionClick}
        disabled={props.disabled}
        className={twMerge([
          "flex w-full items-center justify-center rounded-md border text-sm leading-none focus:outline-none md:text-xs md:font-light",
          props.alone
            ? "px-8 py-3 md:py-3"
            : "px-8 py-3 md:px-1.5 md:py-2 lg:px-3 lg:py-1.5",
          props.disabled
            ? "border-slate-200 bg-slate-100 text-slate-400"
            : "border-slate-300 bg-white text-slate-500 shadow-sm hover:border-slate-400 hover:bg-blue-50 hover:text-slate-700 hover:shadow-none",
        ])}
      >
        <span className="inline-flex h-full w-full items-center justify-center space-x-1">
          {props.children}
        </span>
      </button>
    </motion.div>
  )
}
const ProdAction = props => {
  const { stateCart, stateBag } = useContext(StateContext)
  const [cart] = stateCart
  const [bag] = stateBag
  return (
    <div className="col-span-1 flex items-center justify-center space-x-1.5 md:col-span-2 md:flex-col md:space-x-0 md:space-y-2 lg:ml-4 xl:ml-8">
      {bag !== undefined && bag.length === 0 ? (
        <React.Fragment>
          {/* Quote Button */}
          <ActionButton
            key="initialQuote"
            type="quote"
            actionClick={props.quoteClick}
          >
            <ShoppingBagIcon className="h-4 w-4 stroke-current text-blue-600" />
            <span>Get Quote</span>
          </ActionButton>

          {/* Cart Button */}
          <ActionButton
            key="initialOrder"
            type="order"
            actionClick={props.cartClick}
          >
            <ShoppingCartIcon className="h-4 w-4 stroke-current text-blue-500" />
            <span>Add to Cart</span>
          </ActionButton>
        </React.Fragment>
      ) : cart ? (
        <ActionButton
          key="activeQuote"
          type="order"
          disabled={props.disabled}
          actionClick={props.cartClick}
        >
          {props.disabled ? (
            <>
              <ShoppingCartIcon className="h-4 w-4 stroke-current text-slate-300" />{" "}
              <span>In Cart</span>
            </>
          ) : (
            <>
              <ShoppingCartIcon className="h-4 w-4 stroke-current text-blue-500" />{" "}
              <span>Add to Cart</span>
            </>
          )}
        </ActionButton>
      ) : (
        <ActionButton
          key="activeQuote"
          type="quote"
          disabled={props.disabled}
          actionClick={props.quoteClick}
        >
          {props.disabled ? (
            <>
              <ShoppingBagIcon className="h-4 w-4 stroke-current text-slate-300" />
              <span>In Quote</span>
            </>
          ) : (
            <>
              <ShoppingBagIcon className="h-4 w-4 stroke-current text-blue-600" />
              <span>Quote Item</span>
            </>
          )}
        </ActionButton>
      )}
    </div>
  )
}

// bits and peices

// detail peices
const Title = props => {
  return (
    <div
      className={twMerge([
        "mb-1.5 text-center text-base font-medium leading-5 text-blue-600 md:mb-0 md:text-left md:text-sm",
        props.className,
      ])}
    >
      {props.children}
    </div>
  )
}

const Sterile = props => {
  return (
    <div className="mx-auto flex shrink-0 md:mx-0">
      <span
        className={twMerge([
          "inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold leading-5",
          props.sterile === true || props.sterile === "Yes"
            ? "bg-emerald-100 text-emerald-800"
            : "bg-violet-100 text-violet-800",
        ])}
      >
        {props.sterile === true || props.sterile === "Yes"
          ? `sterile`
          : `non-sterile`}
      </span>
    </div>
  )
}

const Pn = () => {
  return (
    <span className="mr-1 text-slate-400">
      pn
      <HashtagIcon className="inline-block h-3 w-3 fill-current"></HashtagIcon>
    </span>
  )
}

const Pipe = () => {
  return <span className="mx-2 font-extralight text-slate-400">|</span>
}

const Case = () => {
  return <span className="ml-1 text-sm text-slate-400">/case</span>
}

//Specification bits
const SpecItem = props => {
  return (
    <td
      className={twMerge([
        "w-1/3 bg-white px-2 py-2 text-xs font-semibold text-slate-700 md:w-1/4",
        props.className,
      ])}
    >
      {props.children}
    </td>
  )
}
const Spec = props => {
  return (
    <td
      className={twMerge([
        "w-2/3 bg-white px-2 py-2 text-xs font-normal text-slate-600 md:w-3/4",
        props.className,
      ])}
    >
      {props.children}
    </td>
  )
}

export default ProductList
